'use client';

import { useCallback, useState } from "react";
import { AddBox, AddPhotoAlternate, ArrowBack, ArrowForward, DeleteOutline } from "@mui/icons-material";
import { Box, Button, IconButton } from "@mui/material";
import { toast } from "react-toastify";
import type { UploadFile } from "@/app/lib/generated/graphql-types";
import type { ImageCarouselEditableProps } from "@/app/features/shared/image-carusel/types/types";
import { MediaBrowserV2Dialog } from "@/app/features/shared/media-browser-v2";
import ImageCarouselBase from "@/app/features/shared/image-carusel/ui/shared/ImageCarouselBase.client";

export default function ImageCarouselEditable({ images, onImagesChanged }: ImageCarouselEditableProps) {
    const [isMediaBrowserOpen, setIsMediaBrowserOpen] = useState(false);

    const handleImageSelected = useCallback((image: UploadFile | null) => {
        if (!image) return;
        if (!image.mime.startsWith("image/")) {
            toast.error("Bitte wählen Sie eine Bilddatei aus.");
            return;
        }

        onImagesChanged([image, ...images]);
        setIsMediaBrowserOpen(false);
    }, [images, onImagesChanged]);

    const handleImageRemoved = useCallback((index: number) => {
        onImagesChanged(images.filter((_, imageIndex) => imageIndex !== index));
    }, [images, onImagesChanged]);

    const handleImageMoved = useCallback((index: number, direction: -1 | 1) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= images.length) return;

        const reorderedImages = [...images];
        [reorderedImages[index], reorderedImages[targetIndex]] = [
            reorderedImages[targetIndex],
            reorderedImages[index],
        ];
        onImagesChanged(reorderedImages);
    }, [images, onImagesChanged]);

    return (
        <>
            <ImageCarouselBase
                images={images}
                leadingSlide={(
                    <div className="flex-none w-[300px] h-[300px] px-2">
                        <Button
                            variant="outlined"
                            startIcon={<AddPhotoAlternate fontSize="large" />}
                            className="w-full h-full"
                            onClick={() => setIsMediaBrowserOpen(true)}
                            sx={{
                                backgroundColor: 'background.dark',
                                borderColor: 'breakout.main',
                                borderRadius: '8px',
                                borderStyle: 'dashed',
                                borderWidth: 2,
                                color: 'breakout.main',
                                flexDirection: 'column',
                                gap: 1,
                                '& .MuiButton-startIcon': { margin: 0 },
                                '&:hover': {
                                    borderColor: 'breakout.light',
                                    borderStyle: 'dashed',
                                    borderWidth: 2,
                                    color: 'breakout.light',
                                },
                            }}
                        >
                            Bild hinzufügen
                        </Button>
                    </div>
                )}
                renderImageActions={(_, index) => (
                    <Box
                        role="group"
                        aria-label={`Aktionen für Bild ${index + 1}`}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 16,
                            zIndex: (theme) => theme.zIndex.tooltip,
                            display: 'flex',
                            gap: 0.5,
                        }}
                    >
                        <IconButton
                            aria-label={`Bild ${index + 1} nach links verschieben`}
                            disabled={index === 0}
                            size="small"
                            onClick={() => handleImageMoved(index, -1)}
                            sx={imageActionButtonSx}
                        >
                            <ArrowBack fontSize="small" />
                        </IconButton>
                        <IconButton
                            aria-label={`Bild ${index + 1} nach rechts verschieben`}
                            disabled={index === images.length - 1}
                            size="small"
                            onClick={() => handleImageMoved(index, 1)}
                            sx={imageActionButtonSx}
                        >
                            <ArrowForward fontSize="small" />
                        </IconButton>
                        <IconButton
                            aria-label={`Bild ${index + 1} löschen`}
                            size="small"
                            onClick={() => handleImageRemoved(index)}
                            sx={imageActionButtonSx}
                        >
                            <DeleteOutline fontSize="small" color="error" />
                        </IconButton>
                    </Box>
                )}
            />
            <MediaBrowserV2Dialog
                isOpen={isMediaBrowserOpen}
                onApply={handleImageSelected}
                onClose={() => setIsMediaBrowserOpen(false)}
            />
        </>
    );
}

const imageActionButtonSx = {
    border: 1,
    borderColor: 'breakout.main',
    borderRadius: '2px',
    backgroundColor: 'background.dark',
    color: 'breakout.main',
    '&:hover': {
        backgroundColor: 'background.paper',
        borderColor: 'breakout.light',
        color: 'breakout.light',
    },
};
